import { PoolDetails } from "@/models/pool-details.model";
import { Pool } from "@/models/pool.model";
import pacAlerts from "@/assets/conf/pac_alerts.json";
import i18n from "@/core/i18n";
import { getOutByIndex } from "@/shared/utils/outs";
import { getRename } from "@/shared/utils/renames";
import { getProbeLabel } from "@/shared/utils/probes";

const { t } = i18n.global;

const parseInitial = (initial: number): Date => {
  // "initial" est un entier qui contient un datecode tel qu'il est implémenté
  // dans le coffret. les 16 bits de poids faibles représentent le nombre de "10mins"
  // depuis le debut de l'année, Le poids fort le nombre d'années depuis 2000.

  const years = initial >> 16;
  const tenMins = initial & 0xffff;
  const now = new Date();
  const initialDate = new Date();
  initialDate.setTime((tenMins * 10 + now.getTimezoneOffset()) * 60 * 1000);
  initialDate.setFullYear(years + 2000);
  return initialDate;
};

export const deserializeAlert = (alert: PoolDetails["alerts"][number]) => {
  return {
    ...alert,
    initialDate: parseInitial(alert.initial),
  };
};

// --- Logique d'affichage des alertes spéciales ---

const PAC_CODES = [50, 51, 52, 61];
const PAC_TYPE_NAMES = ["KlereoTherm", "InoPAC", "Polytropic"];

const PUMP_ERR_COUNT = 16;
const BSV_ERR_COUNT = 14;

// Mappe l'index device radio (alert.param des codes 5/45) vers un index probe_labels.
// -1 = pas de capteur associé → on retombe sur la clé de traduction RFN{param}.
// Reprend RFSources de l'ancienne app (CAPTEUR_AIR=1, AIR2=19, AIR3=20, TEMP4..10=25..31).
const RF_SOURCES = [-1, 1, -1, -1, -1, -1, 19, 20, 25, 26, 27, 28, 29, 30, 31];

type PacAlert = {
  errorCode: number;
  bit: number;
  pacType: number;
  code?: string;
  text?: string;
};

// Retourne la ligne d'affichage complète pour les alertes à rendu spécial, ou null sinon.
export const getAlertSpecialLine = (
  code: number,
  param: number,
  pacType: number
): string | null => {
  // Alertes PAC bitmask (50, 51, 52, 61)
  if (PAC_CODES.includes(code)) {
    if (pacType > 2) return `Erreur ${code}, code ${param}`;
    const parts: string[] = [];
    for (let bit = 0; bit < 16; bit++) {
      if (!(param & (1 << bit))) continue;
      const entry = (pacAlerts as PacAlert[]).find(
        (e) => e.errorCode === code && e.bit === bit && e.pacType === pacType
      );
      if (!entry) continue;
      if (entry.code && entry.text) parts.push(`${entry.code} (${entry.text})`);
      else if (entry.code) parts.push(entry.code);
      else if (entry.text) parts.push(entry.text);
    }
    if (!parts.length) return null;
    return parts
      .map((p) => `Erreur ${PAC_TYPE_NAMES[pacType]} ${p}`)
      .join("\n");
  }

  // Alertes pompe bitmask (54)
  if (code === 54) {
    const parts: string[] = [];
    let a = param;
    let i = 0;
    while (a > 0) {
      if (a & 1 && i < PUMP_ERR_COUNT) parts.push(t(`PUMP_ERR${i}`));
      i++;
      a >>= 1;
    }
    return parts.length ? parts.join("\n") : null;
  }

  return null;
};

const CAPTEUR_REDOX = 4;

// Certaines alertes remplacent entièrement le libellé Alr{code} (sans capteur ni extension).
// Retourne ce libellé de remplacement, ou null si l'affichage standard s'applique.
export const getAlertNameOverride = (
  code: number,
  param: number
): string | null => {
  if (code === 1 && param === CAPTEUR_REDOX) return t("VLowORP");
  return null;
};

// Retourne l'extension à afficher entre parenthèses après le nom de l'alerte, ou null.
export const getAlertExt = (
  code: number,
  param: number,
  pool: Pool
): string | null => {
  switch (code) {
    // 1/7/8/10/36 (nom du capteur) sont gérés via getProbeLabel dans AlertDetails
    case 5:
    case 45:
      if (param < RF_SOURCES.length) {
        const probeIdx = RF_SOURCES[param];
        return probeIdx >= 0 ? t(getProbeLabel(probeIdx)) : t(`RFN${param}`);
      }
      break;
    case 6:
      if (param === 0) return "pH";
      if (param === 1) return t("CT4");
      break;
    case 35:
      // Nom de la sortie en faute (avec renommage utilisateur éventuel)
      return getRename(pool, 1, param) ?? t(getOutByIndex(param).label);
    case 40:
      if (param < BSV_ERR_COUNT) return t(`BSVE${param}`);
      break;
    case 41:
      if (param === 1) return "Radio";
      if (param === 2) return "Bus";
      break;
    case 13:
    case 14:
    case 53:
      // param affiché sans interprétation
      return `${param}`;
    case 59:
      return `#${param + 1}`;
  }
  return null;
};
